import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Select, { MultiValue, StylesConfig } from 'react-select';
import { useAuth } from '../context/AuthContext';
import { createXperience, getXperienceTags } from '../services/api';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import Input from '../components/Input';
import LocationPicker from '../components/LocationPicker';
import { t } from '../i18n';
import './CreateXperience.css';

const LAST_XPERIENCE_LOCATION_KEY = 'xperience_last_location';

interface TagOption {
  value: number;
  label: string;
}

const customSelectStyles: StylesConfig<TagOption, true> = {
  control: (provided) => ({
    ...provided,
    backgroundColor: 'var(--bg-input, #2a2a2a)',
    borderColor: 'var(--border-color, #444)',
    color: 'var(--text-primary, #fff)',
    boxShadow: 'none',
    '&:hover': {
      borderColor: 'var(--accent-primary, #007bff)'
    }
  }),
  menu: (provided) => ({
    ...provided,
    backgroundColor: 'var(--bg-card, #1e1e1e)',
    border: '1px solid var(--border-color, #444)',
    zIndex: 10
  }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused 
      ? 'var(--accent-transparent, rgba(0, 123, 255, 0.1))'
      : 'transparent',
    color: 'var(--text-primary, #fff)',
    '&:active': {
      backgroundColor: 'var(--accent-primary, #007bff)',
    }
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: 'var(--accent-transparent, rgba(0, 123, 255, 0.2))',
    borderRadius: '4px',
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    color: 'var(--text-primary, #fff)',
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    color: 'var(--text-secondary, #aaa)',
    ':hover': {
      backgroundColor: 'var(--accent-primary, #007bff)',
      color: 'white',
    },
  }),
  input: (provided) => ({
    ...provided,
    color: 'var(--text-primary, #fff)',
  }),
};

const CreateXperiencePage: React.FC = () => {
  const { creator, isProfileComplete } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    primaryLongitude: '',
    primaryLatitude: '',
  });
  const [selectedTags, setSelectedTags] = useState<MultiValue<TagOption>>([]);
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [lastXperienceLocation, setLastXperienceLocation] = useState<{ longitude: string; latitude: string } | null>(null);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_XPERIENCE_LOCATION_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw) as { longitude?: string; latitude?: string };
      if (!parsed.longitude || !parsed.latitude) return;
      setLastXperienceLocation({
        longitude: String(parsed.longitude),
        latitude: String(parsed.latitude)
      });
    } catch {
      // Ignore malformed localStorage value.
    }
  }, []);

  useEffect(() => {
    // Load tags when component mounts
    const loadTags = async () => {
      try {
        const response = await getXperienceTags();
        const formattedTags = response.data.tags.map((tag: any) => ({
          value: tag.id,
          label: tag.name
        }));
        setAvailableTags(formattedTags);
      } catch (err) {
        console.error('Failed to load tags:', err);
      }
    };
    
    loadTags();
  }, []);

  useEffect(() => {
    if (!creator) {
      navigate('/login');
      return;
    }
    
    // Redirect to profile if not complete
    if (!isProfileComplete) {
      navigate('/profile', { 
        state: { message: t('createXperience.profileIncomplete') }
      });
      return;
    }
  }, [creator, isProfileComplete, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const tags = selectedTags.map(tag => tag.value);
      const payload = {
        ...formData,
        longitude: formData.primaryLongitude.trim() === '' ? undefined : Number(formData.primaryLongitude),
        latitude: formData.primaryLatitude.trim() === '' ? undefined : Number(formData.primaryLatitude),
        tags
      };
      
      const response = await createXperience(payload as any);

      if (formData.primaryLongitude.trim() !== '' && formData.primaryLatitude.trim() !== '') {
        const lastLocation = {
          longitude: formData.primaryLongitude,
          latitude: formData.primaryLatitude
        };
        localStorage.setItem(LAST_XPERIENCE_LOCATION_KEY, JSON.stringify(lastLocation));
        setLastXperienceLocation(lastLocation);
      }

      navigate(`/xperience/${response.data.xperience.id}`);
    } catch (err: any) {
      setError(t('createXperience.error.create'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLastLocation = () => {
    if (!lastXperienceLocation) {
      setError(t('createXperience.error.noLastLocation'));
      return;
    }

    setError('');
    setFormData((prev) => ({
      ...prev,
      primaryLongitude: lastXperienceLocation.longitude,
      primaryLatitude: lastXperienceLocation.latitude
    }));
  };

  return (
    <div className="create-page">
      <Navbar />
      
      <div className="create-container">
        <div className="create-header">
          <h1 className="create-title">{t('createXperience.title')}</h1>
          <p className="create-subtitle">{t('createXperience.subtitle')}</p>
        </div>

        <form onSubmit={handleSubmit} className="create-form">
          {error && <div className="create-error">{error}</div>}

          <Input
            label={t('createXperience.field.title')}
            name="title"
            placeholder={t('createXperience.placeholder.title')}
            value={formData.title}
            onChange={handleChange}
            required
          />

          <div className="input-wrapper">
            <label className="input-label">{t('createXperience.field.description')}</label>
            <div className="clearable-field clearable-field-textarea">
              <textarea
                className="input clearable-textarea"
                name="description"
                placeholder={t('createXperience.placeholder.description')}
                value={formData.description}
                onChange={handleChange}
                rows={4}
              />
              {formData.description && (
                <button
                  type="button"
                  className="clearable-field-button"
                  onClick={() => setFormData((prev) => ({ ...prev, description: '' }))}
                  aria-label={t('common.clearSearch')}
                  title={t('common.clearSearch')}
                >
                  x
                </button>
              )}
            </div>
          </div>

          <div className="input-wrapper">
            <label className="input-label">{t('createXperience.field.location')}</label>
            <LocationPicker
              longitude={formData.primaryLongitude}
              latitude={formData.primaryLatitude}
              onChange={({ longitude, latitude }) => {
                setFormData((prev) => ({
                  ...prev,
                  primaryLongitude: longitude,
                  primaryLatitude: latitude
                }));
              }}
              extraActions={
                <button
                  type="button"
                  className="location-picker-action-btn"
                  onClick={handleCopyLastLocation}
                  disabled={!lastXperienceLocation}
                >
                  {t('createXperience.copyLastLocation')}
                </button>
              }
            />
          </div>

          <div className="input-wrapper">
            <label className="input-label">{t('createXperience.field.tags')}</label>
            <Select
              isMulti
              name="tags"
              options={availableTags}
              value={selectedTags}
              onChange={setSelectedTags}
              className="basic-multi-select"
              classNamePrefix="select"
              placeholder={t('createXperience.placeholder.tags')}
              styles={customSelectStyles}
            />
            <p className="input-help" style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
              {t('createXperience.help.tags')}
            </p>
          </div>

          <div className="create-actions">
            <Button type="button" variant="ghost" onClick={() => navigate('/')}>
              {t('createXperience.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={loading}>
              {t('createXperience.submit')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateXperiencePage;
